import View from '@play-co/timestep-core/lib/ui/View';
import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import uiConfig from 'src/lib/ui/config';
import { Feature } from 'src/replicant/analytics';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import { generateQRCode, isShareTargetPickerAvailable } from 'src/lib/sharing';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts from 'src/lib/bitmapFonts';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { shareAddFriendInvite } from '../addFriendsV2.share';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import buttons from 'src/lib/ui/config/buttons';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';

type CloseHandler = () => void;

/**
 * Add Friend popup for Add Friends V2.
 * Displays a QR code, information about LINE friends, and sharing options.
 */
export default class PopupAddFriend extends PopupBasic<CloseHandler> {
  private qrCodeImage: ImageView;
  private lineButton: ButtonScaleViewWithText;
  private inviteUrl: string = '';
  private qrCodeDataUrl: string = '';
  private friendRequestOriginFeature?: typeof Feature.PET_DIALOG._;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => {
        opts.close();
      },
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 940,
      darkerBg: false,
      tracking: {
        feature: Feature.INVITE._,
        $subFeature: Feature.INVITE.NATIVE_SHARE,
      },
    });

    const boxWidth = uiConfig.popups.buruburu.width;
    this.buttonClose.tracking = {
      feature: Feature.INVITE._,
      $subFeature: Feature.INVITE.NATIVE_SHARE,
    };

    // QR Code message
    new LangBitmapFontTextView({
      superview: this.box,
      x: boxWidth / 2,
      y: 120,
      width: boxWidth - 60,
      height: 100,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      font: bitmapFonts('Body'),
      color: '#BF8147',
      centerOnOrigin: true,
      localeText: () => i18n('friends.addFriendPopup.qrMessage'),
    });

    // QR Code with rounded corners frame (tappable for fullscreen view)
    const qrCodeSize = 256;
    const qrCodeFrame = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/friends/popupAddFriend/qrcode_frame.png',
      x: boxWidth / 2,
      y: 320,
      width: qrCodeSize + 32,
      height: qrCodeSize + 32,
      centerOnOrigin: true,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 40, right: 40 },
        vertical: { top: 40, bottom: 50 },
      },
      imgScale: DOWNSCALE_FACTOR,
    });

    // Make the frame tappable for fullscreen popup
    qrCodeFrame.onInputSelect = async () => {
      if (!this.qrCodeDataUrl) return;
      sounds.sfx.play('ok');
      await openPopupPromise('popupAddFriendQRFullscreen', {
        qrCodeDataUrl: this.qrCodeDataUrl,
      });
    };

    this.qrCodeImage = new ImageView({
      superview: qrCodeFrame,
      width: qrCodeSize,
      height: qrCodeSize,
      centerOnOrigin: true,
      x: qrCodeFrame.style.width / 2,
      y: qrCodeFrame.style.height / 2 - 10,
    });

    // LINE separator dividing QR code section from description section
    new View({
      superview: this.box,
      x: 10,
      y: 480,
      width: boxWidth - 20,
      height: 5,
      backgroundColor: '#FDC381',
    });

    // Description text
    new LangBitmapFontTextView({
      superview: this.box,
      x: boxWidth / 2,
      y: 560,
      width: boxWidth - 60,
      height: 60,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: bitmapFonts('Body'),
      color: '#BF8147',
      centerOnOrigin: true,
      localeText: () => i18n('friends.addFriendPopup.description'),
    });

    const info = new ImageScaleView({
      superview: this.box,
      x: 40,
      y: 620,
      width: 495,
      height: 160,
      image: 'assets/ui/friends/popupAddFriend/base.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 50, right: 50 },
        vertical: { top: 50, bottom: 50 },
      },
      imgScale: DOWNSCALE_FACTOR,
    });

    const infoContent = new FlexBoxView({
      superview: info,
      x: 10,
      y: 0,
      width: info.style.width - 20,
      height: info.style.height - 50,
      flex: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 40,
      },
    });

    // Share icon
    const shareIcon = new ImageScaleView({
      superview: infoContent,
      width: downscale(75),
      height: downscale(85),
      image: 'assets/ui/friends/popupAddFriend/icon_share.png',
      scaleMethod: 'contain',
    });

    // Share label inside icon
    new LangBitmapFontTextView({
      superview: shareIcon,
      x: shareIcon.style.width / 2,
      y: 80,
      width: 120,
      height: 50,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      font: bitmapFonts('Body'),
      color: '#BF8147',
      centerOnOrigin: true,
      leading: -5,
      localeText: () => i18n('friends.addFriendPopup.shareLabel'),
    });

    // Arrow 1
    new ImageScaleView({
      superview: infoContent,
      width: downscale(45),
      height: downscale(35),
      image: 'assets/ui/friends/popupAddFriend/icon_arrow.png',
      scaleMethod: 'contain',
    });

    // Tap icon
    const tapIcon = new ImageScaleView({
      superview: infoContent,
      width: downscale(80),
      height: downscale(85),
      image: 'assets/ui/friends/popupAddFriend/icon_tap.png',
      scaleMethod: 'contain',
    });

    // Tap label inside icon
    new LangBitmapFontTextView({
      superview: tapIcon,
      x: tapIcon.style.width / 2,
      y: 80,
      width: 120,
      height: 50,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      font: bitmapFonts('Body'),
      color: '#BF8147',
      centerOnOrigin: true,
      leading: -5,
      localeText: () => i18n('friends.addFriendPopup.tapLabel'),
    });

    // Arrow 2
    new ImageScaleView({
      superview: infoContent,
      width: downscale(45),
      height: downscale(35),
      image: 'assets/ui/friends/popupAddFriend/icon_arrow.png',
      scaleMethod: 'contain',
    });

    // Friends icon
    const friendsIcon = new ImageScaleView({
      superview: infoContent,
      width: downscale(113),
      height: downscale(85),
      image: 'assets/ui/friends/popupAddFriend/icon_friends.png',
      scaleMethod: 'contain',
    });

    // Friends label inside icon
    new LangBitmapFontTextView({
      superview: friendsIcon,
      x: friendsIcon.style.width / 2,
      y: 80,
      width: 120,
      height: 50,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      font: bitmapFonts('Body'),
      color: '#BF8147',
      centerOnOrigin: true,
      leading: -5,
      localeText: () => i18n('friends.addFriendPopup.friendsLabel'),
    });

    // Show native share button only when Share Target Picker is available
    const showNativeShareButton: boolean = isShareTargetPickerAvailable();

    // Calculate button positions
    const lineButtonWidth = downscale(605);
    const lineButtonHeight = downscale(127);
    const nativeShareButtonWidth = downscale(126);
    const nativeShareButtonHeight = downscale(119);
    const buttonSpacing = 12;

    // LINE button x position: centered when alone, shifted left when native share button is visible
    const lineButtonX = showNativeShareButton
      ? boxWidth / 2 - (nativeShareButtonWidth + buttonSpacing) / 2
      : boxWidth / 2;

    // LINE button (Share Target Picker)
    this.lineButton = new ButtonScaleViewWithText({
      superview: this.box,
      x: lineButtonX,
      y: 850,
      width: lineButtonWidth,
      height: lineButtonHeight,
      centerOnOrigin: true,
      ...buttons.secondary,
      icon: {
        image: 'assets/ui/friends/popupAddFriend/line_icon.png',
        width: downscale(87),
        height: downscale(102),
        offset: { x: -150, y: -4 },
      },
      fontSize: 28,
      font: 'Body',
      pressedOffsetY: 5,
      labelOffsetX: 30,
      localeText: () => i18n('friends.addFriendPopup.lineButtonLabel'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.handleShare();
      },
      tracking: {
        feature: Feature.INVITE._,
        $subFeature: Feature.INVITE.NATIVE_SHARE,
      },
    });

    // Native Share button (only visible when Share Target Picker is available)
    if (showNativeShareButton) {
      const nativeShareButtonX =
        lineButtonX +
        lineButtonWidth / 2 +
        buttonSpacing +
        nativeShareButtonWidth / 2;

      new ButtonScaleView({
        superview: this.box,
        x: nativeShareButtonX,
        y: 850,
        width: nativeShareButtonWidth,
        height: nativeShareButtonHeight,
        centerOnOrigin: true,
        image: 'assets/ui/friends/popupAddFriend/blue_button.png',
        imagePressed:
          'assets/ui/friends/popupAddFriend/blue_button_pressed.png',
        imageDisabled: 'assets/ui/shared/buttons/btn_blue_up.png',
        scaleMethod: '9slice' as const,
        icon: {
          image: 'assets/ui/friends/popupAddFriend/share_icon.png',
          width: downscale(65),
          height: downscale(62),
        },
        sourceSlices: {
          horizontal: { left: 35, right: 35 },
          vertical: { top: 35, bottom: 35 },
        },
        imgScale: 0.66,
        pressedOffsetY: 1,
        onClick: async () => {
          sounds.sfx.play('ok');
          await this.handleNativeShare();
        },
        tracking: {
          feature: Feature.INVITE._,
          $subFeature: Feature.INVITE.NATIVE_SHARE,
        },
      });
    }
  }

  protected getAssetsGroup(): AssetGroup | null {
    return new AssetGroup(
      AssetGroup.constructURLs(['assets/ui/friends/popupAddFriend']),
    );
  }

  /**
   * Initialize the popup with QR code and invite URL
   * @param args.inviteUrl - The invite URL for sharing
   * @param args.qrCodeUrl - Optional URL to use for QR code generation. If not set, falls back to inviteUrl
   */
  public async init(args: {
    inviteUrl: string;
    qrCodeUrl?: string;
    origin: 'launch' | 'hud';
    friendRequestOriginFeature?: typeof Feature.PET_DIALOG._;
  }) {
    super.init({
      title: i18n('friends.addFriendPopup.title'),
    });

    // Store the invite URL
    this.inviteUrl = args.inviteUrl;
    this.friendRequestOriginFeature = args.friendRequestOriginFeature;

    // Generate QR code from qrCodeUrl if provided, otherwise use inviteUrl
    const qrCodeDataUrl = await generateQRCode(
      args.qrCodeUrl ?? this.inviteUrl,
      { width: 256, margin: 2 },
    );

    if (qrCodeDataUrl) {
      this.qrCodeDataUrl = qrCodeDataUrl;
      this.qrCodeImage.setImage(qrCodeDataUrl);
    }
  }

  /**
   * Handle Share button click (uses Share Target Picker when available)
   */
  private async handleShare() {
    if (!this.inviteUrl) return;
    await shareAddFriendInvite(
      this.inviteUrl,
      this.friendRequestOriginFeature
        ? { originFeature: this.friendRequestOriginFeature }
        : undefined,
    );
  }

  /**
   * Handle Native Share button click (bypasses Share Target Picker)
   */
  private async handleNativeShare() {
    if (!this.inviteUrl) return;
    await shareAddFriendInvite(
      this.inviteUrl,
      this.friendRequestOriginFeature
        ? {
            forceNativeShare: true,
            originFeature: this.friendRequestOriginFeature,
          }
        : { forceNativeShare: true },
    );
  }
}
